import React, { useLayoutEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { NavigationClient } from "@azure/msal-browser";
import { MsalProvider, useIsAuthenticated, useMsal } from "@azure/msal-react";
import CryptoJS from "crypto-js";
import { CustomToaster } from "utils/reusableToast";

import OrgsList from "components/orgList/orgList";
import RequireEcrOpsRole from "components/RequirePermission/RequireEcrOpsRole";

import { setSessionStartedAt } from "state/slices/sessionSlice";
import { readPersistedSessionStartedAt } from "utils/sessionTiming";

import AdminLayout from "./components/AdminLayout/AdminLayout";
import Layout from "./components/Layout/Layout";
import NotFound from "./components/NotFound/NotFound";
import RequireAuth from "./components/RequireAuth/RequireAuth";
import RequireOrgRole from "./components/RequirePermission/RequireOrgRole";
import RequireOrgOwnerRole from "./components/RequirePermission/RequireOrgOwnerRole";
import RequireGlobalOrgReadRole from "./components/RequirePermission/RequireGlobalOrgReadRole";
import CopilotDashboard from "./pages/admin/CopilotDashboard";
import CopilotUserActivity from "./pages/admin/CopilotUserActivity";
import InactiveUsers from "./pages/admin/InactiveUsers";
import PendingApprovalsTabs from "./pages/admin/components/PendingApprovalsTabs/PendingApprovalsTabs";
import Home from "./pages/home/home";
import InactiveLogout from "./pages/home/InactiveLogout/InactiveLogout";
import HomeDashboard from "./pages/homeDashboard/HomeDashboard";
import OrgHistory from "./pages/OrgHistory/OrgHistory";
import OrgSettings from "./pages/orgSettings/OrgSettings";
import OpsOrgDetail from "./pages/opsDashboard/OpsOrgDetail";
import OpsOrgList from "./pages/opsDashboard/OpsOrgList";
import EcrOpsLayout from "./pages/ecrOps/EcrOpsLayout";
import EcrOpsHome from "./pages/ecrOps/EcrOpsHome";
import EcrOpsOrgManagement from "./pages/ecrOps/EcrOpsOrgManagement";
import EcrOpsUnifiedSearch from "./pages/ecrOps/EcrOpsUnifiedSearch";
import ActiveDirectoryUserLookup from "./pages/ecrOps/ActiveDirectoryUserLookup";
import EcrOpsNewOrgs from "./pages/ecrOps/EcrOpsNewOrgs";
import EcrOpsCompleteOrg from "./pages/ecrOps/EcrOpsCompleteOrg";
import EcrOpsHealthStatus from "./pages/ecrOps/EcrOpsHealthStatus";
import EcrOpsPortalConfig from "./pages/ecrOps/EcrOpsPortalConfig";
import SyncOperationsPage from "./pages/ecrOps/sync/SyncOperationsPage";
import { RequireSyncAccess } from "./pages/ecrOps/sync/syncAccess";
import EcrOpsCopilotEnterprise from "./pages/ecrOps/EcrOpsCopilotEnterprise";
import EcrOpsCostCenters from "./pages/ecrOps/EcrOpsCostCenters";
import EcrOpsCostCenterDetail from "./pages/ecrOps/EcrOpsCostCenterDetail";
import EnterpriseMeteredUsage, { EnterpriseMeteredExportHistory, EnterpriseMeteredUsageUser } from "./pages/meteredUsage/EnterpriseMeteredUsage";
import OrgMeteredUsage, { OrgMeteredUsageUser } from "./pages/meteredUsage/OrgMeteredUsage";
import { RequireMeteredUsageAccess } from "./pages/meteredUsage/meteredAccess";
import AccessManagementLayout from "./pages/ecrOps/accessManagement/AccessManagementLayout";
import RoleAssignmentsPage from "./pages/ecrOps/accessManagement/RoleAssignmentsPage";
import RolesPage from "./pages/ecrOps/accessManagement/RolesPage";
import OrgAccessPage from "./pages/admin/components/OrgAccess/OrgAccessPage";
import RequireAccessManagementRole from "./components/RequirePermission/RequireAccessManagementRole";
import RequireLeadershipRole from "./components/RequirePermission/RequireLeadershipRole";
import LeadershipLayout from "./pages/leadership/LeadershipLayout";
// import DocsHome from "./pages/docs/DocsHome";
// import BlogPost from "./pages/docs/BlogPost";
// import HowToPage from "./pages/docs/HowToPage";
// import WhatIsECR from "./pages/docs/WhatIsECR";
// import PricingPage from "./pages/docs/PricingPage";
import { setAuthState } from "./state/slices/auth/authSlice";

import "./App.css";

class CustomNavigationClient extends NavigationClient {
  constructor(navigate) {
    super();
    this.navigate = navigate; // Passed in from useNavigate hook provided by react-router-dom;
  }
  // This function will be called anytime msal needs to navigate from one page in your application to another
  async navigateInternal(url, options) {
    // url will be absolute, you will need to parse out the relative path to provide to the history API
    const relativePath = url.replace(window.location.origin, "/");
    if (options.noHistory) {
      this.navigate(relativePath, { replace: true });
    } else {
      this.navigate(relativePath);
    }

    return false;
  }
}

function AppRoutes({ msalInstance }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Set the custom navigation client on the msal instance
  const navigationClient = new CustomNavigationClient(navigate);
  msalInstance.setNavigationClient(navigationClient);

  // Get encrypted auth token from localstorage, decrypt, and store in auth slice to rehydrate state on refresh
  // This is a temporary solution until we implement refresh tokens
  const authToken = useSelector((state) => state.auth.authToken);
  const sessionStartedAt = useSelector((state) => state.session.sessionStartedAt);
  if (!authToken && localStorage.getItem("authStateEncrypted")) {
    const decryptedAuthToken = CryptoJS.AES.decrypt(
      localStorage.getItem("authStateEncrypted"),
      "someKey"
    ).toString(CryptoJS.enc.Utf8);

    dispatch(setAuthState(JSON.parse(decryptedAuthToken)));
  }
  // Rehydrate the session-start timestamp the same way — a hard refresh
  // wipes Redux state, but must NOT reset the 1hr session ceiling, so this
  // reads back the persisted value rather than re-stamping "now" (that
  // stamping only happens once, at genuine fresh sign-in — see home.js).
  if (sessionStartedAt == null) {
    const persisted = readPersistedSessionStartedAt();
    if (persisted != null) {
      dispatch(setSessionStartedAt(persisted));
    }
  }

  // scroll to top of page after a page transition.
  useLayoutEffect(() => {
    document.documentElement.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Home />} />


      {/* Protected Routes */}
      <Route path="/*" element={<RequireAuth />}>

        <Route element={<Layout />}>
          <Route path="dashboard" element={<HomeDashboard />} />
          {/* Global org list — every org's name/owners/approvers/countries,
              plus an inline edit action for orgs the viewer can actually
              update (see orgList.jsx's own per-row permission check).
              Gated on organization:read so a user with no org-read access
              anywhere can't reach it at all — RequireGlobalOrgReadRole checks
              for that permission via ANY role, global or per-org, since
              per-org roles (e.g. OrganizationOwner) grant organization:read
              too but aren't part of RequirePermission's global-only check. */}
          <Route path="global-orgs" element={<RequireGlobalOrgReadRole />}>
            <Route index element={<OrgsList />} />
          </Route>
          {/* <Route path="docs" element={<DocsHome />} /> */}
          {/* <Route path="docs/blog/:slug" element={<BlogPost />} />
          <Route path="docs/how-to-users" element={<HowToPage audience="users" />} />
          <Route path="docs/how-to-owners" element={<HowToPage audience="owners" />} />
          <Route path="docs/what-is-ecr" element={<WhatIsECR />} />
          <Route path="docs/pricing" element={<PricingPage />} /> */}
          {/* ECR Ops Portal — all ops tools live under /ecr-ops.
              Restricted to OperationsAdmin / EnterpriseAdmin / OperationsUser,
              replacing the previous REACT_APP_SPECIFIC_USER_EMAILS allowlist. */}
          <Route
            path="ecr-ops"
            element={<RequireEcrOpsRole />}
          >
            <Route element={<EcrOpsLayout />}>
              <Route index element={<EcrOpsHome />} />
              <Route path="org-management" element={<EcrOpsOrgManagement />} />
              {/* Unified search — single page, all sources */}
              <Route path="search" element={<EcrOpsUnifiedSearch />} />
              {/* Legacy route - redirect or keep for backwards compatibility */}
              <Route path="ad-user-lookup" element={<ActiveDirectoryUserLookup />} />
              <Route path="new-orgs" element={<EcrOpsNewOrgs />} />
              <Route path="complete-org/:orgId" element={<EcrOpsCompleteOrg />} />
              <Route path="health" element={<EcrOpsHealthStatus />} />
              <Route path="copilot-enterprise" element={<EcrOpsCopilotEnterprise />} />
              <Route path="cost-centers" element={<EcrOpsCostCenters />} />
              <Route path="cost-centers/:costCenterId" element={<EcrOpsCostCenterDetail />} />
              <Route element={<RequireMeteredUsageAccess />}>
                <Route path="metered-usage" element={<EnterpriseMeteredUsage />} />
                <Route path="metered-usage/exports" element={<EnterpriseMeteredExportHistory />} />
                <Route path="metered-usage/users/:userId" element={<EnterpriseMeteredUsageUser />} />
              </Route>
              <Route path="portal-config" element={<EcrOpsPortalConfig />} />
              {/* Sync Operations — Enterprise Admin / Operations Admin only */}
              <Route element={<RequireSyncAccess />}>
                <Route path="sync" element={<SyncOperationsPage />} />
              </Route>
              <Route path="stuck-users" element={<OpsOrgList />} />
              <Route path="stuck-users/:orgId" element={<OpsOrgDetail />} />
              {/* Access Management — RBAC roles & role assignments (platform-wide),
                  restricted to Enterprise Admin / Operations Admin only */}
              <Route path="access-management" element={<RequireAccessManagementRole />}>
                <Route element={<AccessManagementLayout />}>
                  <Route index element={<Navigate to="role-assignments" replace />} />
                  <Route path="role-assignments" element={<RoleAssignmentsPage />} />
                  <Route path="roles" element={<RolesPage />} />
                </Route>
              </Route>
            </Route>
          </Route>
          {/* Leadership — a global role, but NOT one of the three ECR Ops
              roles (see RequireEcrOpsRole's own comment on why Leadership is
              deliberately excluded from that cross-org override). Leadership
              gets exactly one page — Copilot Usage — via its own guard and
              minimal layout, entirely separate from /ecr-ops, so it never
              gains access to anything else under that subtree. */}
          <Route path="leadership" element={<RequireLeadershipRole />}>
            <Route element={<LeadershipLayout />}>
              <Route index element={<Navigate to="copilot-usage" replace />} />
              <Route path="copilot-usage" element={<EcrOpsCopilotEnterprise />} />
            </Route>
          </Route>
          {/* Role-Based Protected Routes — org Owner/Approver, or a global
              role whose permissions grant view/edit access (see
              RequireOrgRole/RequireOrgOwnerRole for the real, API-backed check
              this replaces the old state.auth.permissions-based RequireRoles with) */}
          <Route
            path="admin/:orgId"
            element={<RequireOrgRole />}
          >
            <Route element={<AdminLayout />}>
              {/* Owner and Approver Routes */}
              <Route path="dashboard" element={<PendingApprovalsTabs />} />
              <Route path="history" element={<OrgHistory />} />
              <Route path="inactive-users" element={<InactiveUsers />} />
              <Route path="copilot-dashboard" element={<CopilotDashboard />} />
              <Route path="copilot-users" element={<CopilotUserActivity />} />
              <Route path="access" element={<OrgAccessPage />} />
              <Route element={<RequireMeteredUsageAccess />}>
                <Route path="metered-usage" element={<OrgMeteredUsage />} />
                <Route path="metered-usage/users/:userId" element={<OrgMeteredUsageUser />} />
              </Route>

              {/* Owner only Routes */}
              <Route
                path=""
                element={<RequireOrgOwnerRole />}
              >
                <Route path="settings" element={<OrgSettings />} />
              </Route>
            </Route>
          </Route>

          {/* Catch all */}
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>

      {/* Catch all */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App({ msalInstance }) {
  // It's important that the Router component is above the Example component because you'll need to use the useHistory hook before rendering MsalProvider

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <MsalProvider instance={msalInstance}>
        <CustomToaster />
        <InactiveLogout>
          <AppRoutes msalInstance={msalInstance} />
        </InactiveLogout>
      </MsalProvider>
    </Router>
  );
}

export default App;
